import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  Alert,
  AlertIcon,
  Progress,
  useColorModeValue
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { imageService } from '../../services/imageService';

export const ImageUpload = ({
  value,
  onChange,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  placeholder = "Click to upload an image",
  aspectRatio = "16/9",
  maxWidth = "400px",
  isRequired = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(value || null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBorderColor = useColorModeValue('gray.300', 'gray.500');
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const hoverBgColor = useColorModeValue('gray.100', 'gray.600');

  useEffect(() => {
    setPreviewUrl(value || null);
  }, [value]);

  // Prevent default drag and drop behavior on the document
  useEffect(() => {
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener('dragover', preventDefaults);
    document.addEventListener('drop', preventDefaults);

    return () => {
      document.removeEventListener('dragover', preventDefaults);
      document.removeEventListener('drop', preventDefaults);
    };
  }, []);

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate the file
    const validation = imageService.validateImageFile(file);
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    setError('');

    // Create preview
    const preview = imageService.createPreviewUrl(file);
    setPreviewUrl(preview);

    // Call onChange with the file
    onChange(file);

    // If onUploadComplete is provided, automatically upload
    if (onUploadComplete) {
      await handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      onUploadStart && onUploadStart();

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Call the parent's upload handler instead of doing it directly
      await onUploadComplete(file);

      clearInterval(progressInterval);
      setUploadProgress(100);

    } catch (err) {
      setError(err.message || 'Failed to upload image');
      onUploadError && onUploadError(err);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      imageService.revokePreviewUrl(previewUrl);
    }
    setPreviewUrl(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate the file
      const validation = imageService.validateImageFile(file);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }

      setError('');

      // Create preview
      const preview = imageService.createPreviewUrl(file);
      setPreviewUrl(preview);

      // Call onChange with the file
      onChange(file);

      // If onUploadComplete is provided, automatically upload
      if (onUploadComplete) {
        await handleUpload(file);
      }
    }
  };

  return (
    <VStack spacing={3} align="stretch" maxWidth={maxWidth}>
      {error && (
        <Alert status="error" size="sm">
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Box
        position="relative"
        aspectRatio={aspectRatio}
        borderWidth={2}
        borderStyle="dashed"
        borderColor={previewUrl ? 'transparent' : borderColor}
        borderRadius="lg"
        bg={previewUrl ? 'transparent' : bgColor}
        cursor="pointer"
        transition="all 0.2s"
        _hover={{
          borderColor: previewUrl ? 'transparent' : hoverBorderColor,
          bg: previewUrl ? 'transparent' : hoverBgColor
        }}
        onClick={!previewUrl ? handleClick : undefined}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        overflow="hidden"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        {previewUrl ? (
          <>
            <Image
              src={previewUrl}
              alt="Preview"
              width="100%"
              height="100%"
              objectFit="cover"
              borderRadius="lg"
            />
            <Box
              position="absolute"
              top={2}
              right={2}
              display="flex"
              gap={2}
            >
              <IconButton
                icon={<EditIcon />}
                size="sm"
                colorScheme="blue"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                aria-label="Change image"
              />
              <IconButton
                icon={<DeleteIcon />}
                size="sm"
                colorScheme="red"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                aria-label="Remove image"
              />
            </Box>
          </>
        ) : (
          <VStack
            justify="center"
            align="center"
            height="100%"
            spacing={2}
            p={4}
          >
            <Text fontSize="lg" color="gray.500">
              📷
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              {placeholder}
            </Text>
            <Text fontSize="xs" color="gray.400" textAlign="center">
              Drag & drop or click • JPEG, PNG, WebP • Max 5MB
            </Text>
          </VStack>
        )}

        {isUploading && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="lg"
          >
            <VStack spacing={2}>
              <Progress
                value={uploadProgress}
                width="80%"
                colorScheme="blue"
                size="sm"
                borderRadius="full"
              />
              <Text color="white" fontSize="sm">
                Uploading... {uploadProgress}%
              </Text>
            </VStack>
          </Box>
        )}
      </Box>

      {previewUrl && !isUploading && (
        <HStack justify="center">
          <Button
            size="sm"
            variant="outline"
            leftIcon={<EditIcon />}
            onClick={handleClick}
          >
            Change Image
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            leftIcon={<DeleteIcon />}
            onClick={handleRemove}
          >
            Remove
          </Button>
        </HStack>
      )}
    </VStack>
  );
};
